import { Component, OnInit } from '@angular/core';
import { api_routes, db_tables } from '@app/consts';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { EmployeeBackgroundChecksService } from './services/employee-background-checks.service';
import { BackgroundCheck } from './models/employee-background-checks.model';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { defer, forkJoin } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-employee-background-checks',
  templateUrl: './employee-background-checks.component.html',
  styleUrls: ['./employee-background-checks.component.scss']
})
export class EmployeeBackgroundChecksComponent implements OnInit {
  employeeID: string;

  public columns: any[] = [
    { field: "name", title: "Name", tableId: "tfi_BcName" },
    { field: "type", subField: "text", title: "Type", tableId: "tfi_BcType" },
    { field: "completionDate", title: "Completion Date", type: "date", tableId: "tfi_BcCompletionDate" },
    { field: "expiryDate", title: "Expiry Date", type: "date", tableId: "tfi_BcExpiryDate" },
    { field: "renewalDate", title: "Renewal Date", type: "date", tableId: "tfi_BcRenewalDate" },
    { field: "provider", title: "Provider", tableId: "tfi_BcProvider" },
    { field: "comments", title: "Comments", type: "longText", tableId: "tfi_BcComments" },
  ];

  filterCategories: any[] = [
    { field: "name", title: "Name", tableId: "tfi_BcName", type: "String", dataType: 'String' },
    { field: "type.text", title: "Type", tableId: "tfi_BcType", type: "String", dataType: 'String' },
    { field: "completionDate", title: "Completion Date", type: "Date", dataType: 'Date', tableId: "tfi_BcCompletionDate" },
    { field: "expiryDate", title: "Expiry Date", type: "Date", dataType: 'Date', tableId: "tfi_BcExpiryDate" },
    { field: "renewalDate", title: "Renewal Date", type: "Date", dataType: 'Date', tableId: "tfi_BcRenewalDate" },
    { field: "provider", title: "Provider", type: "String", dataType: 'String', tableId: "tfi_BcProvider" },
    { field: "comments", title: "Comments", type: "String", dataType: 'String', tableId: "tfi_BcComments" },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name" },
    { field: "type", sortValue: "type.text" },
    { field: "completionDate", sortValue: "completionDate" },
    { field: "expiryDate", sortValue: "expiryDate" },
    { field: "renewalDate", sortValue: "renewalDate" },
    { field: "provider", sortValue: "provider" },
    { field: "comments", sortValue: "comments" }
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formId: string = 'frm_9ZNXpjRSkVjH4H'
  changeReasonFormId: string = 'frm_vI2eOJ7L2aH34n';
  dialogRef: any;
  formValid: any;
  sortString: string = 'completionDate-desc';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.BACKGROUND_CHECKS}/[entityId]`;
  filterString: string;

  constructor(
    private overlayService: OverlayService,
    private translate: TranslateService,
    private employeeBackgroundChecksService: EmployeeBackgroundChecksService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
      params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.isLoading = true;

    if (this.employeeID) {
      this.getTargetEmployeeTablePermissions();
      this.getBackgroundChecks();
    } else {
      this.isLoading = false;
      this.overlayService.hide();
    }
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if (currentFetchedData !== null) {
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
        .subscribe(
          {
            next: (v) => {
              this.targetEmployeeTablePermissions = v;
            },
            error: (e) => this.isLoadingPermissions = false,
            complete: () => this.isLoadingPermissions = false
          }
        );
    }
  }

  getBackgroundChecks() {
    this.isLoading = true;

    this.employeeBackgroundChecksService.getBackgroundChecks(this.employeeID, this.pageSize, this.skip, this.sortString, this.filterString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  getBackgroundCheck(backgroundCheck?: BackgroundCheck) {
    if (backgroundCheck === undefined) {
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeBackgroundChecksService.getBackgroundCheck(this.employeeID, backgroundCheck.id)
        .subscribe(
          {
            next: (v) => {
              this.openFormDialog(v);
            },
            error: (e) => this.isLoading = false,
            complete: () => this.isLoading = false
          }
        );
    }
  }

  openFormDialog(backgroundCheck?: BackgroundCheck) {
    let formData = {
      id: backgroundCheck ? backgroundCheck.id : null,
      name: backgroundCheck ? backgroundCheck.name : null,
      type: backgroundCheck ? backgroundCheck.type?.id : null,
      completionDate: backgroundCheck ? backgroundCheck.completionDate : null,
      expiryDate: backgroundCheck ? backgroundCheck.expiryDate : null,
      renewalDate: backgroundCheck ? backgroundCheck.renewalDate : null,
      provider: backgroundCheck ? backgroundCheck.provider : null,
      comments: backgroundCheck ? backgroundCheck.comments : null,
      changeReason: null,
      changeReasonComments: null,
    }
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('BackgroundCheck')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any) {
    this.overlayService.show();

    if (formData.id === null) {
      this.employeeBackgroundChecksService.createBackgroundCheck(this.employeeID, formData)
        .subscribe(
          {
            next: (v) => {
              this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
              this.dialogRef.close(true);
              this.getBackgroundChecks();
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }
    else {
      this.employeeBackgroundChecksService.updateBackgroundCheck(this.employeeID, formData.id, formData)
        .subscribe(
          {
            next: (v) => {
              this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
              this.dialogRef.close(true);
              this.getBackgroundChecks();
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }
  }

  deleteBackgroundChecks(backgroundCheckIds: string[]) {
    const observables = backgroundCheckIds.map(selectedItem => defer(() => this.employeeBackgroundChecksService.deleteBackgroundCheck(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getBackgroundChecks();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.getBackgroundChecks();
  }

  sortChange(sortString: string) {
    this.sortString = sortString;
    this.getBackgroundChecks();
  }

  filterCallback(filterString: string) {
    this.filterString = filterString;
    this.skip = 0;
    this.getBackgroundChecks();
  }

}
